import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

import { ocppService } from '../services/ocpp/OcppService';
import { IdlePreviewResponse } from '../services/ocpp/types';

const POLLING_INTERVAL_MS = 5000;

/**
 * Polls the live downtime (idle) preview for a session while `enabled`.
 * Mirrors the 5s cadence used by the charging-session polling.
 */
export const useIdlePreview = (
  sessionId: number | string | null | undefined,
  enabled: boolean,
): { preview: IdlePreviewResponse | null; loading: boolean } => {
  const [preview, setPreview] = useState<IdlePreviewResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  useEffect(() => {
    if (!enabled || sessionId == null) {
      setPreview(null);
      return;
    }

    let cancelled = false;

    const fetchPreview = async () => {
      const id = sessionIdRef.current;
      if (id == null) return;
      try {
        const data = await ocppService.getIdlePreview(id);
        if (!cancelled) setPreview(data);
      } catch (error) {
        Sentry.captureException({
          message: '[ocppService][getIdlePreview]',
          error,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    setLoading(true);
    fetchPreview();
    const intervalId = window.setInterval(fetchPreview, POLLING_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [enabled, sessionId]);

  return { preview, loading };
};
